/**
 * Proxy Adapter - Sprint 31
 *
 * Communication adapter for local development.
 * Routes canister calls through the HTTP proxy server (port 5001).
 *
 * The proxy server translates HTTP requests to DFX CLI commands
 * and returns parsed Candid responses.
 */

import {
    CommunicationAdapter,
    CanisterCallOptions,
    CanisterCallError,
    TimeoutError
} from './types';
import { environment } from '../../config/environment';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_TIMEOUT = 30000; // 30 seconds
const LLM_TIMEOUT = 90000; // 90 seconds for LLM calls

/**
 * Methods that require longer timeouts (LLM-related)
 */
const LONG_TIMEOUT_METHODS = [
    'send_message',
    'analyze_config',
    'generate_response',
    'process_query'
];

// ============================================================================
// Proxy Adapter Implementation
// ============================================================================

/**
 * Proxy adapter for local development
 *
 * Routes all canister calls through the HTTP proxy at port 5001.
 * The proxy handles Candid encoding/decoding and DFX CLI execution.
 */
export class ProxyAdapter implements CommunicationAdapter {
    readonly name = 'ProxyAdapter';
    private readonly proxyUrl: string;
    private initialized = false;

    constructor(proxyUrl?: string) {
        this.proxyUrl = proxyUrl || environment.proxyUrl;
    }

    // ========================================================================
    // CommunicationAdapter Implementation
    // ========================================================================

    async initialize(): Promise<void> {
        // Verify proxy is available
        try {
            const response = await fetch(`${this.proxyUrl}/health`, {
                method: 'GET',
                signal: AbortSignal.timeout(5000)
            });

            if (response.ok) {
                this.initialized = true;
                console.log(`✅ ${this.name} initialized - proxy at ${this.proxyUrl}`);
            } else {
                throw new Error(`Proxy health check failed: ${response.status}`);
            }
        } catch (error) {
            console.warn(`⚠️ ${this.name} initialization failed:`, error);
            // Still mark as initialized - proxy might start later
            this.initialized = true;
        }
    }

    isReady(): boolean {
        return this.initialized;
    }

    async call<T = any>(options: CanisterCallOptions): Promise<T> {
        // Route to update or query based on options
        if (options.isQuery) {
            return this.query<T>(options);
        }
        return this.update<T>(options);
    }

    async query<T = any>(options: CanisterCallOptions): Promise<T> {
        // Proxy doesn't distinguish between query and update
        // The DFX CLI handles this based on the method definition
        return this.makeProxyCall<T>(options);
    }

    async update<T = any>(options: CanisterCallOptions): Promise<T> {
        return this.makeProxyCall<T>(options);
    }

    // ========================================================================
    // Private Methods
    // ========================================================================

    /**
     * Make a call through the proxy server
     */
    private async makeProxyCall<T>(options: CanisterCallOptions): Promise<T> {
        const { canisterName, methodName, args, timeout } = options;

        // Determine timeout
        const effectiveTimeout = timeout ||
            (LONG_TIMEOUT_METHODS.includes(methodName) ? LLM_TIMEOUT : DEFAULT_TIMEOUT);

        // Build URL - try both formats
        const url = `${this.proxyUrl}/${canisterName}/${methodName}`;
        const altUrl = `${this.proxyUrl}/call/${canisterName}/${methodName}`;

        if (environment.debug) {
            console.log(`🔧 [${this.name}] ${canisterName}.${methodName}`);
            console.log(`   URL: ${url}`);
            console.log(`   Args:`, args);
        }

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);

        try {
            // Try primary URL first
            let response = await this.attemptCall(url, args, controller.signal);

            // If 404, try alternate URL format
            if (response.status === 404) {
                response = await this.attemptCall(altUrl, args, controller.signal);
            }

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new CanisterCallError(
                    `HTTP ${response.status}: ${response.statusText}`,
                    canisterName.toString(),
                    methodName
                );
            }

            const result = await response.json();

            if (environment.debug) {
                console.log(`✅ [${this.name}] Response:`, result);
            }

            // Handle proxy response formats
            return this.parseProxyResponse<T>(result, canisterName.toString(), methodName);

        } catch (error: any) {
            clearTimeout(timeoutId);

            if (error.name === 'AbortError') {
                throw new TimeoutError(
                    canisterName.toString(),
                    methodName,
                    effectiveTimeout
                );
            }

            if (error instanceof CanisterCallError) {
                throw error;
            }

            throw new CanisterCallError(
                error.message || 'Unknown error',
                canisterName.toString(),
                methodName,
                error
            );
        }
    }

    /**
     * Attempt a single HTTP call
     */
    private async attemptCall(
        url: string,
        args: any[],
        signal: AbortSignal
    ): Promise<Response> {
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ args }, (_key, value) =>
                typeof value === 'bigint' ? Number(value) : value
            ),
            signal
        });
    }

    /**
     * Parse proxy response into expected format
     */
    private parseProxyResponse<T>(
        result: any,
        canisterName: string,
        methodName: string
    ): T {
        // Handle error responses
        if (result.error) {
            throw new CanisterCallError(
                result.error,
                canisterName,
                methodName
            );
        }

        // Proxy wraps responses in { data, raw, success }
        // The canister-client expects unwrapped data
        if (result && typeof result === 'object') {
            // If has 'data' field, return that (standard proxy response)
            if ('data' in result) {
                return result.data as T;
            }

            // If has 'Ok' field, it's a Result type - return as-is
            if ('Ok' in result || 'Err' in result) {
                return result as T;
            }
        }

        // Return raw result
        return result as T;
    }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a proxy adapter with default configuration
 */
export function createProxyAdapter(): ProxyAdapter {
    return new ProxyAdapter(environment.proxyUrl);
}
